/**
 * Barcodeformate des Handscanners im Verkauf (Go-Live Lebensmittelhandel, SUNMI U2 als
 * USB-HID-Tastatur): EAN-13, EAN-8, UPC-A und UPC-E mit führenden Nullen und Prüfziffer.
 * Reine Funktionen ohne DOM.
 *
 * - **UPC-E** (8 Stellen: Nummernsystem 0/1, sechs Ziffern, Prüfziffer) wird zur UPC-A und
 *   damit zur EAN-13 aufgeweitet; gültig nur, wenn die Prüfziffer der UPC-A passt.
 * - **EAN-8 mit führenden Nullen** (als UPC-A, EAN-13 bzw. GTIN-14 übertragen) wird zur EAN-8.
 * - **Gewichts-/Preis-Barcodes** (EAN-13 mit Präfix 20–29, „Instore“-Codes der Waage) werden
 *   nur erkannt und protokolliert, **nie** als Gewicht oder Preis gedeutet (kein kg aus dem
 *   Code): Ein exakter Stammeintrag gilt wie jeder andere Code, sonst bleibt der Scan ohne
 *   Position mit einer verständlichen Meldung.
 */
import { istGueltigeEan, normalisiereEan } from './lager';

/** UPC-E (8 Stellen mit Nummernsystem 0/1 und Prüfziffer) → UPC-A (12 Stellen); sonst null. */
export function upcEZuUpcA(code: string): string | null {
  const c = code.trim();
  if (!/^[01]\d{7}$/.test(c)) {
    return null;
  }
  const ns = c.charAt(0);
  const d = c.slice(1, 7);
  const pruef = c.charAt(7);
  const letzte = d.charAt(5);
  let kern: string;
  switch (letzte) {
    case '0':
    case '1':
    case '2':
      kern = `${d.slice(0, 2)}${letzte}0000${d.slice(2, 5)}`;
      break;
    case '3':
      kern = `${d.slice(0, 3)}00000${d.slice(3, 5)}`;
      break;
    case '4':
      kern = `${d.slice(0, 4)}00000${d.charAt(4)}`;
      break;
    default:
      kern = `${d.slice(0, 5)}0000${letzte}`;
  }
  const upcA = `${ns}${kern}${pruef}`;
  return istGueltigeEan(`0${upcA}`) ? upcA : null;
}

/**
 * Weitere Vergleichsformen eines gescannten Codes über `normalisiereEan` hinaus (für die
 * Code-Suche, wenn der Rohcode nichts trifft): aufgeweitete UPC-E als EAN-13 und eine mit
 * Nullen aufgefüllte EAN-8 in ihrer 8-stelligen Form. Ohne passende Form leer.
 */
export function zusatzFormen(code: string): string[] {
  const c = code.trim();
  const formen = new Set<string>();
  const upcA = upcEZuUpcA(c);
  if (upcA !== null) {
    formen.add(`0${upcA}`);
  }
  const ean8 = aufgefuellteEan8(c);
  if (ean8 !== null) {
    formen.add(ean8);
  }
  formen.delete(c);
  return [...formen];
}

/** EAN-8, die als 12-, 13- bzw. 14-stelliger Code mit führenden Nullen kommt; sonst null. */
function aufgefuellteEan8(code: string): string | null {
  if (!/^\d{12,14}$/.test(code)) {
    return null;
  }
  const kopf = code.slice(0, -8);
  const ean8 = code.slice(-8);
  return /^0+$/.test(kopf) && istGueltigeEan(ean8) ? ean8 : null;
}

/**
 * Scan mit verlorener erster Ziffer (Android-WebView: die erste Taste nach dem Laden fehlt oder
 * kommt als „Unidentified“): 7 bzw. 12 Ziffern, die selbst keine gültige GTIN sind. Eine EAN-13
 * mit führender 0 bleibt als 12-stellige UPC-A gültig (dieselbe Ware) und zählt nicht dazu.
 */
export function kannVerkuerzteGtinSein(code: string): boolean {
  const c = code.trim();
  return /^(\d{7}|\d{12})$/.test(c) && normalisiereEan(c) === null;
}

/**
 * Form eines Stammcodes ohne erste Ziffer (für {@link kannVerkuerzteGtinSein}): aus einer
 * gültigen EAN-8 bzw. EAN-13 (auch als UPC-A/GTIN-14 gespeichert) die letzten 7 bzw. 12
 * Ziffern; sonst null.
 */
export function ohneErsteZiffer(stammCode: string): string | null {
  const c = stammCode.trim();
  const gtin = /^\d{8}$/.test(c) ? (istGueltigeEan(c) ? c : null) : normalisiereEan(c);
  return gtin === null ? null : gtin.slice(1);
}

/**
 * Gewichts- bzw. Preis-Barcode (EAN-13 mit Präfix 20–29 bzw. als UPC-A mit Nummernsystem 2):
 * Instore-Code, in dem die Waage Artikelnummer und Gewicht oder Preis verschlüsselt. Wird nur
 * erkannt – die Kasse deutet die Ziffern nie als Gewicht oder Preis.
 */
export function istGewichtsPreisCode(code: string): boolean {
  const ean = normalisiereEan(code.trim());
  return ean !== null && ean.length === 13 && /^2\d{12}$/.test(ean);
}

/** Meldung, wenn ein Gewichts-/Preis-Barcode keinen Stammeintrag trifft. */
export function gewichtsPreisHinweis(code: string): string {
  return `Code ${code} ist ein Waagen-Barcode mit Gewicht oder Preis (Präfix 2x) – wird nicht automatisch übernommen; Artikel bitte über Kachel oder Suche wählen`;
}

// ---------------------------------------------------------------------------------------------
// Scanprotokoll: auffällige Scans (Gewichts-/Preis-Barcode, doppelt gemeldeter Scan) für die
// Fehlersuche am Gerät. Nur im Arbeitsspeicher der Kasse (höchstens 200 Einträge) und als
// `console.info` – keine Beleg- oder Umsatzdaten, kein Versand.

export type ScanProtokollArt = 'gewicht-preis' | 'doppelt';

export interface ScanProtokollEintrag {
  art: ScanProtokollArt;
  code: string;
  /** Zeitpunkt (ms, `performance.now()` bzw. Zeitstempel der Taste). */
  zeit: number;
  /** Treffer im Stamm (nur bei `gewicht-preis`). */
  treffer?: number;
}

const MAX_PROTOKOLL = 200;
const protokoll: ScanProtokollEintrag[] = [];

export function protokolliereScan(eintrag: ScanProtokollEintrag): void {
  protokoll.push(eintrag);
  if (protokoll.length > MAX_PROTOKOLL) {
    protokoll.splice(0, protokoll.length - MAX_PROTOKOLL);
  }
  console.info(`[Scanner] ${eintrag.art}: ${eintrag.code}`);
}

/** Kopie des Scanprotokolls (älteste zuerst). */
export function scanProtokoll(): readonly ScanProtokollEintrag[] {
  return [...protokoll];
}

/** Nur für Tests. */
export function scanProtokollLeeren(): void {
  protokoll.length = 0;
}

/**
 * Doppelt gemeldeter Scan: derselbe Code ein zweites Mal binnen dieser Frist (Abschlusszeichen
 * zu Abschlusszeichen). Ein absichtlicher zweiter Scan mit dem Handscanner braucht deutlich
 * länger (Taste loslassen und erneut drücken, ≥ 250 ms) und zählt als zweites Stück.
 */
export const DOPPELSCAN_MS = 120;

/** Merkt den letzten Scan und erkennt doppelte Meldungen desselben Scans. */
export class DoppelScanFilter {
  private letzterCode: string | null = null;
  private letzteZeit = Number.NEGATIVE_INFINITY;

  constructor(private readonly fristMs = DOPPELSCAN_MS) {}

  /** Eine andere Taste der Kassierkraft dazwischen: der nächste Scan ist sicher ein neuer. */
  zuruecksetzen(): void {
    this.letzterCode = null;
  }

  /** true = annehmen; false = doppelte Meldung (wird protokolliert). */

  annehmen(code: string, zeit: number): boolean {
    const doppelt =
      this.fristMs > 0 &&
      code === this.letzterCode &&
      zeit >= this.letzteZeit &&
      zeit - this.letzteZeit <= this.fristMs;
    if (doppelt) {
      protokolliereScan({ art: 'doppelt', code, zeit });
      return false;
    }
    this.letzterCode = code;
    this.letzteZeit = zeit;
    return true;
  }
}
